/**
 * MCP Tool Management Types
 * For dynamic tool and action configuration via FastAPI MCP backend
 */

export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export type APIMethod = HTTPMethod | 'GRAPHQL';

export type ContentType =
  | 'application/json'
  | 'application/x-www-form-urlencoded'
  | 'multipart/form-data'
  | 'text/plain'
  | 'application/graphql';

export type AuthType = 'none' | 'bearer' | 'oauth2_per_user' | 'api_key_header' | 'api_key_query' | 'custom';

export type ResponseProjectionMode = 'singular' | 'list';

/**
 * OAuth Configuration for per-user authentication
 */
export interface OAuthConfig {
  client_id: string;
  client_secret: string;
  auth_uri: string;
  token_uri: string;
  redirect_uri: string;
  scopes: string[];
}

/**
 * Authentication configuration for a tool
 */
export interface AuthConfig {
  auth_type?: AuthType;
  headers?: Record<string, string>;
  query_params?: Record<string, string>;
  credentials?: Record<string, string>;
  oauth_config?: OAuthConfig;
}

/**
 * Response projection field configuration
 */
export interface ProjectionField {
  path: string; // JSONPath expression (e.g., $.email, $.data.name)
  label: string; // User-friendly label
}

/**
 * Response projection configuration
 * Defines how to extract and display data from API responses
 */
export interface ResponseProjection {
  mode: ResponseProjectionMode;
  items_path?: string; // Required when mode === 'list', JSONPath to array
  fields: ProjectionField[];
}

/**
 * GraphQL configuration for GraphQL API calls
 */
export interface GraphQLConfig {
  query: string; // GraphQL query or mutation string
  variables?: Record<string, any>; // Query variables
  operationName?: string; // Optional operation name
}

/**
 * Request configuration for different content types
 */
export interface RequestConfig {
  content_type: ContentType;
  body_format?: 'json' | 'form_data' | 'raw'; // How body should be serialized
  custom_headers?: Record<string, string>; // Additional headers beyond auth
}

/**
 * Auth summary returned per tool from GET /api/tools
 */
export interface MCPToolAuthSummary {
  auth_type: AuthType;
  has_headers: boolean;
  header_keys: string[];
  has_query_params: boolean;
  query_param_keys: string[];
  has_credentials: boolean;
  requires_user_oauth: boolean;
}

/**
 * MCP Action Summary (minimal info for lists)
 */
export interface MCPActionSummary {
  name: string;
  method: HTTPMethod;
  endpoint: string;
  parameters?: any;
  example_response?: any;
  response_projection?: ResponseProjection | null;
  param_configs?: any;
  request_config?: RequestConfig | null;
  graphql_config?: GraphQLConfig | null;
  form_config?: any;
}

/**
 * Full MCP Tool definition
 */
export interface MCPTool {
  managed?: boolean;
  is_active?: boolean;
  id?: string;
  name: string;
  description: string;
  base_url: string;
  auth?: MCPToolAuthSummary;
  auth_configured: boolean;
  auth_type: AuthType;
  requires_user_oauth: boolean;
  actions: MCPActionSummary[];
  created_at?: string;
  updated_at?: string;
  workspace_id?: string;
  created_by_user_id?: string;
}

/**
 * Payload for creating/updating a tool
 */
export interface MCPToolCreate {
  name: string;
  description: string;
  base_url: string;
  auth_config?: AuthConfig;
}

/**
 * Full MCP Action definition
 */
export interface MCPAction {
  id?: string;
  action_name: string;
  endpoint: string; // Relative path, can include {placeholders}
  http_method: HTTPMethod;
  api_method?: APIMethod; // Extended method including GRAPHQL
  parameters?: any; // JSON Schema for parameters
  example_response?: any; // Sample API response
  response_projection?: ResponseProjection;
  graphql_config?: GraphQLConfig; // GraphQL specific configuration
  request_config?: RequestConfig; // Content-Type and request format
  param_configs?: any;
  form_config?: any;
  created_at?: string;
  updated_at?: string;
  tool_name?: string;
  tool_id?: string;
  workspace_id?: string;
  created_by_user_id?: string;
}

/**
 * Payload for creating/updating an action
 */
export interface MCPActionCreate {
  param_configs?: any;
  form_config?: any;
  action_name: string;
  endpoint: string;
  http_method: HTTPMethod;
  api_method?: APIMethod;
  parameters?: any;
  example_response?: any;
  response_projection?: ResponseProjection;
  graphql_config?: GraphQLConfig;
  request_config?: RequestConfig;
}

/**
 * API Response for tool list
 */
export interface MCPToolsListResponse {
  tools: MCPTool[];
  count: number;
}

/**
 * API Response for single tool detail
 */
export interface MCPToolDetailResponse {
  success: boolean;
  tool: MCPTool;
  actions: MCPAction[];
}

/**
 * API Response for tool operations (create/update/delete)
 */
export interface MCPToolOperationResponse {
  success: boolean;
  message: string;
  tool?: MCPTool;
}

/**
 * API Response for action operations
 */
export interface MCPActionOperationResponse {
  success: boolean;
  message: string;
  action?: MCPAction;
}

/**
 * Form state for tool creation wizard
 */
export interface MCPToolFormState {
  step: number;
  basicInfo: {
    name: string;
    description: string;
    base_url: string;
  };
  authConfig: AuthConfig;
  isValid: boolean;
}

/**
 * Form state for action creation
 */
export interface MCPActionFormState {
  action_name: string;
  endpoint: string;
  http_method: HTTPMethod;
  parameters?: string; // JSON string
  example_response?: string; // JSON string
  response_projection: ResponseProjection;
}

/**
 * Auth mode options for UI selection
 */
export const AUTH_MODE_OPTIONS = [
  { value: 'none', label: 'No Authentication', description: 'Public API with no auth required' },
  { value: 'api_key_header', label: 'API Key (Header)', description: 'API key sent in Authorization header' },
  { value: 'api_key_query', label: 'API Key (Query Param)', description: 'API key sent as query parameter' },
  { value: 'bearer', label: 'Bearer Token', description: 'Bearer token authentication' },
  { value: 'custom', label: 'Custom Headers', description: 'Custom header configuration' },
  { value: 'oauth2_per_user', label: 'OAuth 2.0 (Per User)', description: 'User-level OAuth authentication' },
] as const;

/**
 * HTTP method options for UI
 */
export const HTTP_METHOD_OPTIONS: HTTPMethod[] = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

/**
 * API method options including GraphQL
 */
export const API_METHOD_OPTIONS: APIMethod[] = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'GRAPHQL'];

/**
 * Content type options for UI
 */
export const CONTENT_TYPE_OPTIONS = [
  { value: 'application/json', label: 'JSON', description: 'JSON request body' },
  { value: 'application/x-www-form-urlencoded', label: 'Form URL Encoded', description: 'Standard form data' },
  { value: 'multipart/form-data', label: 'Multipart Form', description: 'File uploads and form data' },
  { value: 'text/plain', label: 'Plain Text', description: 'Raw text body' },
  { value: 'application/graphql', label: 'GraphQL', description: 'GraphQL query' },
] as const;

/**
 * Response projection mode options
 */
export const PROJECTION_MODE_OPTIONS = [
  { value: 'singular', label: 'Single Object', description: 'Response is a single object' },
  { value: 'list', label: 'List/Array', description: 'Response contains a list of items' },
] as const;

// ============================================
// Smart Forms Enhancement Types
// ============================================

/**
 * Parameter value source - where to get the value from
 */
export type ParameterSource =
  | 'direct_input'      // User types the value
  | 'context_token'     // From Mem0/session token like {{user.email}}
  | 'previous_action'   // From result of previous action in workflow
  | 'conversation'      // Extract from conversation context
  | 'ai_decide';        // Let AI agent decide the value

/**
 * Fallback behavior when parameter value is not found
 */
export type ParameterFallback =
  | 'error'             // Show error and stop execution
  | 'default_value'     // Use a default value
  | 'ai_decide'         // Let AI agent decide
  | 'human_handoff';    // Hand off to human agent

/**
 * Parameter configuration with source and fallback
 */
export interface ParameterConfig {
  source: ParameterSource;
  value?: any;                    // Direct value or token template
  fallback: ParameterFallback;
  default_value?: any;            // Used when fallback is 'default_value'
  required: boolean;              // Is this parameter required
  let_ai_decide: boolean;         // Should AI fill this value
  handoff_to_human: boolean;      // Should human agent provide this
}

/**
 * Field specification for smart form generation
 * Describes a single parameter field with metadata
 */
export interface FieldSpec {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  description?: string;
  default?: any;
  placeholder?: string;
  transform?: 'stripe_query' | 'json_nested'; // Special handling for Stripe queries or nested objects
  enum?: string[];
  pattern?: string; // Regex pattern for validation
  nested?: FieldSpec[]; // For object/array types
  config?: ParameterConfig; // Parameter configuration
}

/**
 * Form model generated from action metadata
 * Represents the complete form structure based on HTTP method and auth config
 */
export interface FormModel {
  method: HTTPMethod;
  endpoint: string;
  pathParams: FieldSpec[]; // Extracted from {placeholders} in endpoint
  queryParams: FieldSpec[]; // For GET requests or additional query strings
  bodyFields: FieldSpec[]; // For POST/PUT/PATCH request bodies
  authBadges: AuthBadge[]; // Auth status indicators
  contextTokens: ContextToken[]; // Available Mem0 tokens
}

/**
 * Authentication status badge
 * Shows OAuth connection status and prompts for missing auth
 */
export interface AuthBadge {
  type: 'configured' | 'missing' | 'oauth_required' | 'oauth_connected' | 'oauth_disconnected';
  message: string;
  actionRequired?: boolean; // If true, user needs to take action (e.g., connect OAuth)
  actionLabel?: string; // Label for the action button (e.g., "Connect Stripe")
}

/**
 * Context token from Mem0 episodic memory
 * Provides reusable values like {{user.email}}, {{workspace.domain}}
 */
export interface ContextToken {
  key: string; // Token key, e.g., "user.email"
  value: string; // Actual value or preview
  source: 'mem0' | 'session' | 'workspace'; // Where the token comes from
  insertTemplate: string; // Template string to insert, e.g., "{{user.email}}"
  description?: string; // Human-readable description
}

/**
 * Stripe-specific query parameter helpers
 * Pre-defined filters for common Stripe list operations
 */
export interface StripeQueryHelper {
  key: string; // Query param key, e.g., "email", "created[gte]"
  label: string; // Display label, e.g., "Email", "Created After"
  type: 'string' | 'number' | 'timestamp';
  placeholder?: string;
  description?: string;
}

/**
 * Predefined Stripe query helpers for common filters
 */
export const STRIPE_QUERY_HELPERS: StripeQueryHelper[] = [
  { key: 'email', label: 'Email', type: 'string', placeholder: 'user@example.com' },
  { key: 'name', label: 'Name', type: 'string', placeholder: 'John Doe' },
  { key: 'created[gte]', label: 'Created After', type: 'timestamp', description: 'Unix timestamp' },
  { key: 'created[lte]', label: 'Created Before', type: 'timestamp', description: 'Unix timestamp' },
  { key: 'limit', label: 'Limit', type: 'number', placeholder: '10', description: 'Max results' },
];

/**
 * Parameter source options for UI
 */
export const PARAMETER_SOURCE_OPTIONS = [
  { value: 'direct_input', label: 'Direct Input', description: 'Enter the value manually', icon: 'Type' },
  { value: 'context_token', label: 'Context Token', description: 'Use {{token}} from memory', icon: 'Sparkles' },
  { value: 'previous_action', label: 'Previous Action', description: 'Use result from previous action', icon: 'ArrowRight' },
  { value: 'conversation', label: 'From Conversation', description: 'Extract from chat context', icon: 'MessageSquare' },
  { value: 'ai_decide', label: 'AI Decides', description: 'Let AI determine the value', icon: 'Brain' },
] as const;

/**
 * Parameter fallback options for UI
 */
export const PARAMETER_FALLBACK_OPTIONS = [
  { value: 'error', label: 'Show Error', description: 'Stop execution and show error' },
  { value: 'default_value', label: 'Use Default', description: 'Use a default value' },
  { value: 'ai_decide', label: 'Let AI Decide', description: 'AI determines fallback value' },
  { value: 'human_handoff', label: 'Human Handoff', description: 'Ask human agent for value' },
] as const;

/**
 * Parameter normalization mode
 * Maps to backend _normalize_parameters logic
 */
export type ParameterNormalizationMode = 'path' | 'query' | 'body';

/**
 * Normalized parameter payload
 * Output format aligned with backend's _normalize_parameters
 */
export interface NormalizedParameters {
  path_params: Record<string, any>; // {customer_id: "cus_123"}
  query_params: Record<string, any>; // {email: "test@example.com", limit: 10}
  body: Record<string, any> | null; // Request body for POST/PUT/PATCH
}

// ============================================
// Test Execution Types
// ============================================

/**
 * Test execution request payload
 */
export interface ActionTestRequest {
  parameters: NormalizedParameters;
  graphql_config?: GraphQLConfig; // For GraphQL tests
}

/**
 * Test execution response from backend
 */
export interface ActionTestResponse {
  success: boolean;
  status_code: number;
  response: any; // Raw API response
  projected_data?: any; // Response after projection applied
  execution_time_ms: number;
  error?: string;
  headers?: Record<string, string>; // Response headers
}
